/**
 * @module experience/landing
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable:no-any */ // Need to support all data types
// Core
import { Injectable } from '@angular/core';
// Services
import { API_MAP, ApiGatewayService } from '@core/public';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { UserService } from '@support/shared/services/user.service';
// Interfaces
import { APIError } from '@support/api/common/error.interface';
import { LineInfo } from '@experience/repdash/interfaces/API-231-get-lines.interface';
import { LOST_STOLEN_CONSTANT } from '@experience/landing/common/constants/lost-stolen.constants';
import { RemoveSuspension } from '@support/api/proxies/responses/api-316.interface';
import { CustomerLineBillingDetailsAccountLines } from '@support/api/proxies/responses/api-2008.interface';
import { ManageBlackListResponse } from '@support/api/proxies/responses/api-2045.interface';
import { ChangeLineCartResponse } from '@support/api/proxies/responses/api-2033-cart.interface';
import {
  Line,
  CustomerAccountLineDeviceDetails,
} from '@support/api/proxies/responses/api-2022.interface';
// Constants
import { LineSummary } from '@support/api/proxies/responses/api-2006.interface';
import { throwError as observableThrowError, of as observableOf, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
@Injectable()
export class RemoveSuspensionService {
  public imei: string;

  constructor(
    private stateStoreUtilsService: StateStoreUtilsService,
    private apiGatewayService: ApiGatewayService,
    private objectService: ObjectService,
    private userService: UserService
  ) {
    this.imei = '';
  }

  /**
   * Function to remove suspension from Lost/Stolen device/service
   * @returns Observable<RemoveSuspension>
   */
  public restorePhoneServices(): Observable<RemoveSuspension> {
    const currentLineInfo = this.getCustomerDetails();
    const lineInfo = this.getLineBillingDetails();
    const request = {
      lineId: currentLineInfo.lineOfServiceId,
      msisdn: currentLineInfo.msisdn,
      lineName: lineInfo.lineNickname,
      imei: this.getImei(),
      browserDate: new Date().toLocaleDateString(),
      blockDevice: LOST_STOLEN_CONSTANT.NO,
      lostStolenSuspend: LOST_STOLEN_CONSTANT.NO,
      deviceStatus: LOST_STOLEN_CONSTANT.REACTIVE_STATUS,
    };
    const pathParams = {
      customerId: this.stateStoreUtilsService.getCustomerId(),
      accountId: this.stateStoreUtilsService.getAccountId(),
    };
    return this.apiGatewayService
      .postRequest(API_MAP.API_316_postRemoveSuspension, request, null, null, pathParams)
      .pipe(
        map((response: HttpResponse<RemoveSuspension>) => {
          return response.body;
        }),
        catchError(() => {
          return observableOf({ success: false });
        })
      );
  }

  /**
   * Function to set black-list device for lost-stolen
   * @param {boolean} blockImei
   * @returns {Observable<ManageBlackListResponse>}
   */
  public postManageBlackList(blockImei: boolean): Observable<ManageBlackListResponse> {
    const request = {
      imei: this.getImei(),
      reasonCode: LOST_STOLEN_CONSTANT.STOLEN /* 4 is the  Reason code for stolen */,
      action: blockImei ? LOST_STOLEN_CONSTANT.BLOCK : LOST_STOLEN_CONSTANT.UNBLOCK,
      customerId: this.stateStoreUtilsService.getBscsCustomerID(),
      accountNumber: this.stateStoreUtilsService.getAccountId(),
    };

    return this.apiGatewayService.postRequest(API_MAP.API_2045_manageBlackList, request).pipe(
      map((response: HttpResponse<Object>) => {
        return response.body;
      }),
      catchError((error: APIError[]) => {
        return observableThrowError(error);
      })
    );
  }
  /**
   * Function to restore services for a given line
   * @returns {Observable<ChangeLineCartResponse>}
   */
  public restoreServices(): Observable<ChangeLineCartResponse> {
    const request = {
      accountNumber: this.stateStoreUtilsService.getAccountId(),
      ccid: this.stateStoreUtilsService.getBscsCustomerID(),
      changeTypes: LOST_STOLEN_CONSTANT.RESTORE,
      lineOfServiceId: this.stateStoreUtilsService.getLineId(),
      reasonCode: LOST_STOLEN_CONSTANT.FAC,
      imei: this.getImei(),
      termsAndconditions: true,
    };
    return this.userService.postChangeLine(request);
  }
  /**
   * Method to get Imei details based on msisdn
   * @returns string
   */
  public getImei(): string {
    const msisdn = this.stateStoreUtilsService.getMsisdn();
    const deviceInfo = this.stateStoreUtilsService.getCustomerAccountLineDeviceDetailsData(msisdn);
    if (deviceInfo && deviceInfo.devicedetails && deviceInfo.devicedetails.lines) {
      return (
        deviceInfo.devicedetails.lines.find(
          (line: Line) => line.msisdn === this.stateStoreUtilsService.getMsisdn()
        ).imei || ''
      );
    }
  }

  /**
   * Function to get current customer details from cache (retreived from 2007 API)
   * @returns LineDetails
   */
  public getCustomerDetails(): LineSummary {
    const accountLinesInfo = this.stateStoreUtilsService.getBillingBlockDetails();
    const lines = this.objectService.get(accountLinesInfo, 'lines');
    if (lines) {
      return lines.find(
        (line: LineSummary) => line.lineOfServiceId === this.stateStoreUtilsService.getLineId()
      );
    } else {
      return null;
    }
  }

  /**
   * Function to get current customer line details from cache (retreived from 2008 API)
   * @returns LineDetails
   */
  public getLineBillingDetails(): CustomerLineBillingDetailsAccountLines {
    const accountLinesInfo = this.stateStoreUtilsService.getLineBillingDetails();
    const lines = this.objectService.get(accountLinesInfo, 'account.lines');
    if (lines) {
      return lines.find(
        (line: CustomerLineBillingDetailsAccountLines) =>
          line.lineID === this.stateStoreUtilsService.getLineId()
      );
    } else {
      return null;
    }
  }

  /**
   * Function to get Line information from cache (retreived from 231 API)
   * @returns Line
   */
  public getLineInfo(): LineInfo {
    const linesInfo = this.stateStoreUtilsService.getLinesInfo();
    const lines = this.objectService.get(linesInfo, 'lines');
    if (lines) {
      return lines.find((line: LineInfo) => line.id === this.stateStoreUtilsService.getLineId());
    } else {
      return null;
    }
  }
  /**
   * Function to get device details from API-2022
   * @returns {Observable<ChangeLineCartResponse>}
   */
  public getDeviceDetails(msisdn: string): Observable<CustomerAccountLineDeviceDetails> {
    return this.userService.getCustomerAccountLineDeviceDetails(msisdn);
  }
}
